import {KnowledgeRagTopic} from 'common/src/modules/ai/ai.enums';
import {FeatureFlagKey} from 'common/src/modules/featureFlags/featureFlags.types';
import isFlagEnabled from 'common/src/modules/featureFlags/utils/isFlagEnabled';
import {CLStore} from 'common/src/redux/store';
import {z, ZodTypeAny} from 'zod';
import currentRouteContext from '../../contexts/currentRouteContext/currentRouteContext';
import generalContext from '../../contexts/generalContext';
import {AgentName, LLMResponseType} from '../../copilot.enums';
import {plaintextPartSchema} from '../../copilot.schemas';
import {Agent, AgentContext, ConversationStarter, Tool} from '../../copilot.types';
import getDefaultLLMModel from '../../models/utils/getDefaultLLMModel';
import getLLMModel from '../../models/utils/getLLMModel';
import {OpenAIModel} from '../../openAI/openAI.enums';
import {OpenAIModelConfig} from '../../openAI/openAI.models';
import {generalKnowledgeTool} from '../../tools/generalKnowledgeTool';
import resourceKnowledgeTool from '../../tools/resourceKnowledgeTool/resourceKnowledgeTool';
import buildInstructions from '../buildInstructions';
import agentBase from './agentBase';

type CreateAgentParams = {
  name: AgentName;
  role: string;
  instructions?: string;
  inScope: string[];
  outOfScope?: string[];
  knowledgeTopics?: KnowledgeRagTopic[];
  contexts?: AgentContext[];
  model?: OpenAIModelConfig;
  classificationModel?: OpenAIModelConfig;
  __DEPRECATED_uiToolNames?: string[];
  widgetSchemas?: ZodTypeAny[];
  tools?: Tool<ZodTypeAny>[];
  store: CLStore;
  conversationStarters?: ConversationStarter[];
};

export default function agent({
  name,
  role,
  inScope,
  tools = [],
  instructions,
  store,
  conversationStarters = [],
  outOfScope = [],
  knowledgeTopics = [],
  __DEPRECATED_uiToolNames = [],
  widgetSchemas,
  contexts = [],
  model = getDefaultLLMModel(),
  classificationModel,
}: CreateAgentParams): Agent {
  classificationModel ??= getLLMModel(OpenAIModel.Gpt5_1);

  const agentTools = [...tools, generalKnowledgeTool()];

  if (isFlagEnabled(FeatureFlagKey.EnableCopilotResourceKnowledge)) {
    agentTools.push(resourceKnowledgeTool());
  }

  const agentContexts = [generalContext, currentRouteContext(store.getState), ...contexts];

  const responseSchema = _buildContentSchema(widgetSchemas as [ZodTypeAny, ...ZodTypeAny[]]);

  const fullInstructions = buildInstructions({
    agent: name,
    role,
    scope: {inScope, outOfScope},
    instructions,
  });

  const agentBaseObject = agentBase({
    name,
    role,
    instructions: fullInstructions,
    knowledgeTopics,
    tools: agentTools,
    model,
    classificationModel,
    responseSchema,
  });

  return {
    ...agentBaseObject,
    name,
    scope: {inScope, outOfScope},
    __DEPRECATED_uiToolNames,
    contexts: agentContexts,
    store,
    conversationStarters,
  };
}

function _buildContentSchema(
  additionalContentPartSchemas?: [ZodTypeAny, ...ZodTypeAny[]],
): ZodTypeAny {
  const partsSchema = additionalContentPartSchemas
    ? z.union([plaintextPartSchema, ...additionalContentPartSchemas])
    : plaintextPartSchema;

  return z.object({
    type: z
      .literal(LLMResponseType.Content)
      .describe(`A '${LLMResponseType.Content}' type of response`),
    parts: z.array(partsSchema),
  });
}
